import {useEffect, useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import debounce from 'lodash/debounce';

import {ApiStatus} from '../../../models';
import {updateBlock, updatedBlock} from '../../../store/actions/blockActions';
import {getBlockSelector} from '../../../store/reducers/blockReducer';
import {IState} from '../../../store/reducers';
import {updateDotNotationObject} from '../utils';

export function useUpdateBlockContentJson(
  uid: string | undefined,
  readOnly: boolean = false,
  autosave: boolean = true,
  debounceTime: number = 800,
): any {
  const [updating, setUpdating] = useState(false);
  const updatingStatus = useSelector((state: IState) => state.block.updatingStatus);
  const block = useSelector(getBlockSelector(uid as string));
  const dispatch = useDispatch();
  /*
   * Cache contentJson in order to have always the latest version, not just after debounce.
   */
  const [contentJson, setContentJson] = useState(block?.content_json);

  const updateBlockFn = useCallback((uid: string, newBlock: any) => {
    setUpdating(true);
    dispatch(updateBlock(uid, newBlock));
  }, []);
  const debounceUpdateBlockFn = useMemo(() => debounce(updateBlockFn, debounceTime), []);
  /*
   * If autosave is disabled, we don't want to go through the whole flow of updating the block via API,
   *   we just want to update it in redux store.
   */
  const updatedBlockFn = useCallback((newBlock: any, fields: string[]) => {
    dispatch(updatedBlock(newBlock, fields));
  }, []);
  const debounceUpdatedBlockFn = useMemo(() => debounce(updatedBlockFn, debounceTime) , []);

  useEffect(() => {
    if (updatingStatus !== ApiStatus.LOADING) {
      if (updatingStatus === ApiStatus.LOADED || updatingStatus === ApiStatus.FAILED) {
        setUpdating(false);
      }
    }
  }, [updatingStatus]);

  const updateBlockContentJson = (
    key: string | string[],
    value: any | any[],
    useDebounce: boolean = true,
  ) => {
    /*
     * `key` and `value` can be either string or array of strings.
     * If they are array, they must have the same length. In this case,
     *   multiple `content_json` keys will be updated at the same time.
     */
    if (updating || !uid) {
      return;
    }

    const _contentJson = contentJson || block?.content_json || {};

    let newContentJson: any = {};
    if (Array.isArray(key)) {
      newContentJson = key.reduce((acc: any, k, index) => {
        acc[k] = value[index];
        return acc;
      }, {});
    } else if (key.includes('.')) {
      newContentJson = updateDotNotationObject(JSON.parse(JSON.stringify(_contentJson)), key, value);
    } else {
      newContentJson[key] = value;
    }

    if (readOnly) {
      return false; // do not trigger update
    }

    /*
     * Don't forget to stringify the content_json
     */
    newContentJson = JSON.stringify({
      ..._contentJson,
      ...newContentJson,
    });
    let newBlock: any = {
      content_json: newContentJson,
    };

    setContentJson(JSON.parse(newContentJson));

    if (!autosave) {
      /*
       * If autosave is disabled, we only update the local state with `setContentJson`,
       *   and update the block in the redux store, skipping the API call.
       */
      newBlock = {
        ...newBlock,
        uid: uid,
      }
      const fields = ['content_json'];

      if (useDebounce) {
        debounceUpdatedBlockFn(newBlock, fields);
      } else {
        updatedBlockFn(newBlock, fields);
      }
      return; // do not trigger API update
    }

    if (useDebounce) {
      debounceUpdateBlockFn(uid, newBlock);
    } else {
      updateBlockFn(uid, newBlock);
    }
  };

  return {
    updating,
    updateBlockContentJson,
  };
}
